import { createContext, useContext } from "react";

/**
 * Mode « réglage des droits » de la fiche bien.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POURQUOI CE MODE PLUTÔT QU'UNE PAGE SÉPARÉE
 * ─────────────────────────────────────────────────────────────────────────────
 * L'ancienne page des droits était une liste de 252 clés empilées, maintenue à
 * la main dans `BIEN_SECTIONS`. Deux défauts, tous deux constatés en prod le
 * 2026-08-10 :
 *
 *  1. Elle DÉRIVE. Sa structure n'a aucun lien mécanique avec la fiche : des
 *     clés y survivaient sans champ (`jeNaiPasFaitEle`), d'autres y portaient
 *     un libellé inversé (« Prestataire ELS » pour le prestataire précédent).
 *  2. Elle oblige à connaître le nom technique d'un champ pour le retrouver
 *     parmi 252 lignes — alors que plus de la moitié ne concernent même pas la
 *     fiche location.
 *
 * D'où ce mode : la page de droits ne RESSEMBLE pas à la fiche, elle EST la
 * fiche. `BienDetailPage` se monte avec ce contexte, et le helper `F()` — par
 * lequel passent 105 des 113 champs — rend un sélecteur à trois états au lieu
 * du champ. Onglets, blocs, grille et ordre : identiques par construction. La
 * dérive devient impossible.
 */

/**
 * Les trois états qu'un admin peut donner à un champ.
 *
 * Remplace le couple `can_see` / `can_edit`, qui autorisait quatre
 * combinaisons dont une absurde (« modifiable mais invisible »). Vérifié en
 * base avant la bascule : 0 ligne sur 2713 était dans cet état, la conversion
 * ne perd donc rien.
 */
export type PermState = "edit" | "view" | "hidden";

export function toPermState(canSee: boolean, canEdit: boolean): PermState {
  if (canEdit) return "edit";
  return canSee ? "view" : "hidden";
}

export function fromPermState(state: PermState): { can_see: boolean; can_edit: boolean } {
  switch (state) {
    case "edit": return { can_see: true, can_edit: true };
    case "view": return { can_see: true, can_edit: false };
    case "hidden": return { can_see: false, can_edit: false };
  }
}

/**
 * Préréglages de popup fiche bien — Apparts for Rent et Pipeline ELS. Ne
 * sont PAS des rôles : ils narrowent (jamais n'élargissent) ce que le rôle
 * réel `collaborateur_afr` voit déjà, uniquement dans la popup ouverte
 * depuis ces deux écrans. Cf. docs/superpowers/specs/2026-08-18-droits-
 * location-visibilite-contextuelle-design.md.
 */
export type FichePreset = "apparts_for_rent" | "pipeline_els";

/**
 * Entrées "préréglage" du déroulant de rôle de l'éditeur de droits. Chacune
 * partage le même rôle effectif (`collaborateur_afr`, cf.
 * ROLE_EFFECTIF_FICHE) mais écrit dans `bien_fiche_presets` au lieu de
 * `field_permissions` — voir DroitsChampsPage.
 */
export const DROITS_PRESET_ROLES = ["collaborateur_apparts", "collaborateur_pipeline"] as const;
export type DroitsPresetRole = (typeof DROITS_PRESET_ROLES)[number];

export const PRESET_OF_ROLE: Record<DroitsPresetRole, FichePreset> = {
  collaborateur_apparts: "apparts_for_rent",
  collaborateur_pipeline: "pipeline_els",
};

/**
 * Rôles « humains » proposés dans le déroulant de la page de droits.
 *
 * `admin` n'y figure pas : `useFieldPermissions` lui rend `true` sans lire la
 * table, il n'y a donc rien à régler pour lui.
 */
export const DROITS_ROLES_HUMAINS = [
  "c_level", "collaborateur", "publication", "vente", "prestataire",
  "collaborateur_apparts", "collaborateur_pipeline",
] as const;
export type DroitsRoleHumain = (typeof DROITS_ROLES_HUMAINS)[number];

export function isPresetRole(r: DroitsRoleHumain): r is DroitsPresetRole {
  return (DROITS_PRESET_ROLES as readonly string[]).includes(r);
}

/**
 * Rôle humain → rôle RÉELLEMENT lu par la fiche bien.
 *
 * ⚠️ C'est le cœur du problème qui a coûté une demi-journée le 2026-08-10.
 * `useBiensPermRole()` remappe le rôle avant toute lecture de permissions sur
 * un écran « bien ». Un déroulant qui écrirait sur le rôle nommé serait donc
 * silencieusement sans effet pour le collaborateur : la migration 108 en a
 * fait l'expérience, il a fallu la 109 pour rattraper.
 *
 * Cette table DOIT rester le miroir de `useBiensPermRole()` pour le chemin
 * `/v2/biens/:id`. `publication` n'y est pas remappé — et depuis le 2026-08-10
 * ce n'est plus une subtilité mais un fait simple : son remap par chemin
 * (`/planning-photos`, `/suivi-publication`) était mort, la migration 113 a
 * fusionné ses deux sous-rôles dans la ligne `publication` et la 114 les a
 * supprimés. Il ne reste qu'un seul rôle de publication.
 */
export const ROLE_EFFECTIF_FICHE: Record<DroitsRoleHumain, string> = {
  c_level: "c_level",
  collaborateur: "collaborateur_afr",
  publication: "publication",
  vente: "vente",
  prestataire: "prestataire",
  // Les deux préréglages montent la fiche avec le rôle réel le plus complet
  // de la famille collaborateur — impossible d'élargir au-delà de ce qu'il
  // voit déjà (règle de non-élargissement de la spec).
  collaborateur_apparts: "collaborateur_afr",
  collaborateur_pipeline: "collaborateur_afr",
};

export interface FieldPermsEditor {
  /** Rôle en cours de réglage — le rôle EFFECTIF, pas le rôle humain choisi. */
  role: string;
  /** État courant d'un champ. */
  get: (fieldKey: string) => PermState;
  /** L'admin change l'état d'un champ. */
  set: (fieldKey: string, state: PermState) => void;
  /**
   * Déclare qu'un champ est actuellement À L'ÉCRAN, avec son libellé lisible.
   *
   * C'est ainsi que la page apprend la liste réelle des champs : elle ne la
   * déduit pas d'un registre parallèle, elle la reçoit du rendu de la fiche.
   *
   * La fiche ne rendant que l'onglet actif, l'ensemble des champs enregistrés
   * à un instant donné EST l'onglet courant — ce qui donne les actions
   * groupées par onglet sans avoir à modéliser les onglets ici.
   */
  register: (fieldKey: string, label: string) => void;
  /** Le champ quitte l'écran (changement d'onglet, bloc replié). */
  unregister: (fieldKey: string) => void;
  /**
   * `false` en mode préréglage : l'édition n'est jamais un réglage de
   * préréglage (spec §3, objectif 4). Absent (`undefined`) = `true`, pour ne
   * rien changer aux appelants existants qui éditent un rôle réel.
   */
  allowEdit?: boolean;
}

const FieldPermsEditorContext = createContext<FieldPermsEditor | null>(null);

export const FieldPermsEditorProvider = FieldPermsEditorContext.Provider;

/** `null` en usage normal — la fiche se rend alors comme d'habitude. */
export function useFieldPermsEditor(): FieldPermsEditor | null {
  return useContext(FieldPermsEditorContext);
}
